<template lang="pug">
  .favorites-header
    .favorites-header__folders-missing.folders-missing(
      v-if="isFoldersMissing"
    )
      .folders-missing__wrapper.folders-missing__wrapper--mobile
        BannerCTA(
          :image="foldersMissingImagePath"
          title="Сортируйте свои любимые статьи с&nbsp;помощью папок"
        )
          template(v-slot:footer)
            .folders-missing__new-folder.new-folder
              .new-folder__button(
                v-if="addMode === 'button'"
                @click="addFolder"
              ) Создать папку
              .new-folder__input(
                v-else="addMode === 'input'"
                v-click-outside="onClickOutside"
              )
                AddInput(
                  placeholder="Новая папка"
                  :maxLengthInput="28"
                  theme="medium"
                  :value="nameNewFolder"
                  @input-enter="onInputEnter"
                  @input-escape="onInputEscape"
                  @input="changeFolderName"
                )
      .folders-missing__wrapper.folders-missing__wrapper--desktop
        .folders-missing__image(
          :style="missingImageStyle"
        )
        .folders-missing__content
          .folders-missing__title Сортируйте свои любимые статьи с&nbsp;помощью папок
          .folders-missing__new-folder.new-folder
            .new-folder__button(
              v-if="addMode === 'button'"
              @click="addFolder"
            ) Создать папку
            .new-folder__input(
              v-else="addMode === 'input'"
            )
              AddInput(
                placeholder="Новая папка"
                :maxLengthInput="28"
                theme="medium"
                :value="nameNewFolder"
                @input="changeFolderName"
                @input-enter="onInputEnter"
                @input-escape="onInputEscape"
              )

    .favorites-header__content(
      v-else-if="folderCurrent && folders"
    )
      .favorites-header__select.select
        .select__handler(
          @click="onDropdownClick"
        )
          .select__handler-icon(
            :class="handlerDropdownClass"
          )
          .select__handler-title {{ folderCurrent.name }}
          .select__handler-dropdown
        .select__dropdown(
          v-if="dropdownActive"
          v-click-outside="onSelectHandlerOutside"
        )
          .folder(
            v-for="folder in foldersSorted"
            :key="folder.id"
            @click="onClickFolder(folder)"
          )
            .folder__name(
              :class="getFolderNameClass(folder)"
            )
              span {{ folder.name }}
            .folder__total {{ folder.total }}
      a.favorites-header__control.control(
        v-if="userState"
        :href="`/profile/user/${userState.integer_id}/favorites-edit`"
      )
</template>

<script lang="ts">
  import type { PropType } from 'vue';
  import { vOnClickOutside } from '@vueuse/components';
  import type { User } from '@devhacker/shared/features/user/types/user';
  import BannerCTA from '~/components/BanerCTA.vue';
  import AddInput from '@/components/lh-ui/AddInput/AddInput.vue';
  import { useFoldersStore } from '~/store/folders';
  import { useIndexStore } from '~/store';
  import NoFolders from '~/assets/images/no-folders.svg';

  export default defineNuxtComponent({
    name: 'ProfileFavoritesHeader',

    components: {
      AddInput,
      BannerCTA,
    },

    directives: {
      clickOutside: vOnClickOutside,
    },

    props: {
      folders: {
        type: Array as PropType<IFolderItem[]>,
        required: true,
      },
      folderCurrent: {
        type: Object as PropType<IFolderItem>,
        required: true,
        validator: (prop: null | object) => {
          return prop === null || typeof prop === 'object';
        },
      },
    },

    data: () => ({
      addMode: 'button' as 'button' | 'input',
      dropdownActive: false as boolean,
      nameNewFolder: '' as string,
    }),

    setup() {
      return {
        foldersStore: useFoldersStore(),
        indexStore: useIndexStore(),
      };
    },

    computed: {
      userState(): User | null {
        return this.indexStore.user;
      },

      handlerDropdownClass() {
        return {
          'select__handler-icon--active': this.dropdownActive,
        };
      },

      foldersSorted(): Array<IFolderItem> {
        return this.folders.slice().sort((folder1: IFolderItem, folder2: IFolderItem) => {
          if (folder1.id === null) {
            return -1;
          }
          if (folder2.id === null) {
            return 1;
          }

          if (folder1.id === this.folderCurrent?.id) {
            return -1;
          }

          if (folder2.id === this.folderCurrent?.id) {
            return 1;
          }

          return 0;
        });
      },

      foldersCount(): number {
        return (
          this.folders
            // без учета папки Общее
            .filter((folder: IFolderItem) => folder.id !== null).length
        );
      },

      isFoldersMissing(): boolean {
        return !this.foldersCount && Boolean(this.folderCurrent);
      },

      foldersMissingImagePath() {
        let imagePath = null;

        try {
          imagePath = NoFolders;
        } catch (error) {
          console.error(error);
        }

        return imagePath;
      },

      missingImageStyle(): any {
        return {
          backgroundImage: this.foldersMissingImagePath
            ? `url(${this.foldersMissingImagePath})`
            : 'none',
        };
      },
    },

    watch: {
      folderCurrent() {
        this.dropdownActive = false;
      },
    },

    methods: {
      onClickOutside(): void {
        const isMobile = window.matchMedia('(max-width: 767px)');
        if (!isMobile.matches) return;

        this.addMode = 'button';
      },

      changeFolderName($event) {
        this.nameNewFolder = $event.target.value;
      },

      getFolderNameClass(folder: IFolderItem) {
        return {
          'folder__name--active': folder.id === this.folderCurrent?.id,
        };
      },

      addFolder() {
        this.addMode = 'input';
      },

      isSuccessResponse(response?: { status: number } | null): boolean {
        return !!response && response.status >= 200 && response.status < 300;
      },

      getSuffixName(name: string): string {
        const escapeName = name.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
        const regex = new RegExp(`^${escapeName}(\\s*)?(\\((\\d*)\\))?$`);

        const duplicates = this.folders.filter((folder: IFolderItem) => regex.test(folder.name));

        const indexes = duplicates.map((folder: IFolderItem) => {
          const result = folder.name.match(regex);
          if (result) {
            return Number.isNaN(parseInt(result[3], 10)) ? 0 : parseInt(result[3], 10);
          }
          return 0;
        });

        return indexes.length ? ` (${Math.max(...indexes) + 1})` : '';
      },

      getNewNameFolder(name: string) {
        const newName = name || 'Новая папка';

        return `${newName}${this.getSuffixName(newName)}`;
      },

      async onInputEnter(name: string): Promise<void> {
        const newNameFolder = this.getNewNameFolder(name);

        const response = await this.foldersStore.addFolder(newNameFolder);

        if (this.isSuccessResponse(response)) {
          this.addMode = 'button';
          this.foldersStore
            .getFolders()
            .then(() => {
              this.$emit('create-folder', name);
            });
          this.nameNewFolder = '';
        }
      },

      onInputEscape() {
        this.addMode = 'button';
        this.nameNewFolder = '';
      },

      onDropdownClick() {
        this.dropdownActive = !this.dropdownActive;
      },

      onSelectHandlerOutside() {
        this.dropdownActive = false;
      },

      onClickFolder(folder: IFolderItem): void {
        this.$emit('choose-folder', folder);
      },
    },
  });
</script>

<style lang="scss" scoped>
  @use '@devhacker/shared/styles/scrollbar.scss' as scrollbar;

  .favorites-header {
    $header: &;

    &__content {
      display: flex;
      justify-content: space-between;
      padding: 0 16px;
      position: relative;
      z-index: 5;
    }

    &__select {
      flex-grow: 1;

      & + #{$header}__control {
        margin-left: 40px;
      }
    }
  }

  .folders-missing {
    $folders-missing: &;

    &__wrapper {
      &--mobile {
        display: block;

        @include tablet {
          display: none;
        }
      }

      &--desktop {
        display: none;

        @include tablet {
          display: flex;
          align-items: center;
          background-color: #fff;
          padding: 0 16px;
          margin: 0 auto;
          width: 416px;
        }
      }
    }

    &__content {
      display: flex;
      flex-direction: column;
      justify-content: center;
      margin-left: 24px;
      margin-top: 28px;
      margin-bottom: 28px;
    }

    &__image {
      width: 56px;
      height: 56px;
      background-repeat: no-repeat;
      background-size: contain;
      background-position: center center;

      @include tablet {
        width: 136px;
        height: 136px;
      }
    }

    &__title {
      @include fontH4AccentSmallText;
      font-weight: normal;
      margin-bottom: 16px;

      @include tablet {
        font-weight: 600;
      }
    }
  }

  .new-folder {
    &__button {
      background-color: #2962f9;
      @include fontTextBtn;
      color: white;
      padding: 12px 16px;
      text-align: center;
      cursor: pointer;
      border-radius: 2px;

      @include hover {
        background-color: #264eb1;
      }
    }
  }

  .select {
    $select: &;
    display: flex;
    align-items: center;
    cursor: pointer;
    user-select: none;
    position: relative;
    width: 100%;

    &__handler {
      $selectHandler: &;

      padding: 12px 0;
      display: flex;
      align-items: center;
      width: 100%;

      @include hover {
        #{$selectHandler} {
          &__icon {
            @include foldersMask;
            background-color: #2962f9;
          }
        }
      }

      &-icon {
        width: 24px;
        height: 24px;
        @include foldersMask;
        background-color: #969698;
        flex-shrink: 0;
        margin-right: 4px;

        &--active {
          background-color: #2962f9;
        }
      }

      &-title {
        @include fontSmallTextBtn;

        display: -webkit-box;
        -webkit-box-orient: vertical;
        overflow: hidden;
        word-break: break-word;
        text-overflow: ellipsis;
        -webkit-line-clamp: 1;

        & + #{$select}__dropdown {
          margin-left: 8px;
        }
      }

      &-dropdown {
        width: 16px;
        height: 16px;
        margin-left: 8px;
        @include dropdownMask;
        background-color: #2962f9;
        flex-shrink: 0;
      }
    }

    &__dropdown {
      position: absolute;
      top: 100%;
      left: 0;
      z-index: 1;
      width: 320px;
      background: #ffffff;
      box-shadow: 0px 3px 10px rgba(0, 0, 0, 0.25);
      border-radius: 4px;
      padding: 8px 0;
      max-height: 440px;

      overflow-y: auto;
      @include scrollbar.customScrollbar;
    }
  }

  .control {
    width: 48px;
    flex-shrink: 0;
    cursor: pointer;
    @include controlMask;
    background-color: #2962f9;
    transition: background-color 0.2s;

    @include hover {
      background-color: #264eb1;
    }
  }

  .folder {
    display: flex;
    justify-content: space-between;
    padding: 16px;
    cursor: pointer;

    @include tablet {
      padding: 12px 24px;
    }

    @include hover {
      background-color: #e1eafd;
    }

    &__name {
      @include fontTextBtn;
      font-weight: 400;
      display: flex;
      align-items: center;
      justify-content: flex-start;
      white-space: nowrap;
      overflow: hidden;

      &--active {
        &:after {
          content: '';
          width: 24px;
          height: 24px;
          display: inline-block;
          @include okMask;
          background-color: #2962f9;
        }
      }

      > span {
        display: inline-block;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      svg {
        flex-shrink: 0;
        width: 24px;
        height: 24px;
      }
    }

    &__total {
      @include fontTextBtn;
      font-weight: 400;
      color: #969698;
      padding-left: 24px;
      flex-shrink: 0;
    }
  }
</style>
